import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { isInsideEditor } from '@dotcms/client';
import { Observable, Subject, takeUntil } from 'rxjs';
import { emitDotCmsEMAEvent, isSiteEmbedded } from 'src/app/extensions/dot-cms/utils/dot-cms-utils';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { AppFacade } from 'ish-core/facades/app.facade';
import { DeviceType } from 'ish-core/models/viewtype/viewtype.types';
import { FeatureToggleService } from 'ish-core/utils/feature-toggle/feature-toggle.service';

type CollapsibleComponent = 'search' | 'navbar' | 'minibasket';

/**
 * The Header Component displays the page header.
 *
 * It uses the {@link LoginStatusComponent} for rendering the users login status.
 * It uses the {@link ProductCompareStatusComponent} for rendering the product compare button and count.
 * It uses the {@link MobileBasketComponent} for rendering the mobile basket button and basket item count.
 * It uses the {@link LanguageSwitchComponent} for rendering the language selection dropdown.
 * It uses the {@link SearchBoxComponent} for rendering the search box.
 * It uses the {@link HeaderNavigationComponent} for rendering the pages main navigation.
 * It uses the {@link MiniBasketComponent} for rendering mini basket on desktop sized viewports.
 *
 * @example
 * <ish-header></ish-header>
 */
@Component({
  selector: 'custom-header-default',
  templateUrl: './custom-header-default.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./custom-header-default.component.scss'],
})
export class CustomHeaderDefaultComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isSticky = false;
  @Input() deviceType: DeviceType;
  @Input() reset: unknown;
  isLoggedIn$: Observable<boolean>;
  activeComponent: CollapsibleComponent = 'search';
  showSearchBox = false;
  showSignIn = false;
  isInsideUveEditor = isInsideEditor();
  @ViewChild('globalNav') containerRef: ElementRef;

  constructor(
    private accountFacade: AccountFacade,
    private featureToggleService: FeatureToggleService,
    private appFacade: AppFacade
  ) {}
  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.isLoggedIn$ = this.accountFacade.isLoggedIn$;
    this.appFacade
      .getSeachActive()
      .pipe(takeUntil(this.destroy$))
      .subscribe(active => (this.showSearchBox = active));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.reset) {
      this.activeComponent = 'search';
    }
    this.toggleSpecialStatusOfSearch();

    if (this.isSticky) {
      this.onScroll();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showSearch() {
    return (
      this.activeComponent === 'search' &&
      // always show for sticky header
      (this.deviceType === 'mobile' || this.isSticky)
    );
  }

  get showNavBar() {
    return (
      this.activeComponent === 'navbar' ||
      // always show for desktop
      this.deviceType === 'desktop'
    );
  }

  private toggleSpecialStatusOfSearch() {
    // deactivate search when switching to sticky header
    if (this.isSticky && this.activeComponent === 'search') {
      this.activeComponent = undefined;
    }
    // activate search when scrolling to top and no other is active
    if (!this.isSticky && !this.activeComponent) {
      this.activeComponent = 'search';
    }
  }

  toggle(component: CollapsibleComponent) {
    if (this.activeComponent === component) {
      // activate search bar when on top and no other is active
      this.activeComponent = !this.isSticky ? 'search' : undefined;
    } else {
      this.activeComponent = component;
    }
  }
  isDotCmsEditView(): boolean {
    if (this.featureToggleService.enabled('dotCms') && isSiteEmbedded()) return true;
    return false;
  }
  emitdotcmsHomePageEditEvent() {
    if (this.isInsideIframe()) {
      emitDotCmsEMAEvent('/index');
    }
  }
  isInsideIframe(): boolean {
    return isSiteEmbedded();
  }

  onScroll() {
    if (!SSR && this.deviceType === 'desktop') {
      const container = this.containerRef?.nativeElement;
      document.getElementById('globalNavigation').classList.add('has-overflow-right');
      document.getElementById('globalNavigation').classList.add('has-overflow-left');
      if (container.scrollLeft === 0) {
        document.getElementById('globalNavigation').classList.remove('has-overflow-left');
      }

      if (container.scrollWidth - container.clientWidth - container.scrollLeft < 4) {
        document.getElementById('globalNavigation').classList.remove('has-overflow-right');
      }
    }
  }
  showOrHideSearchBox(clickedOutside?: string) {
    this.showSearchBox = !this.showSearchBox;
    if (clickedOutside && this.showSearchBox) {
      this.showSearchBox = false;
    }
    this.getSearchBoxAnimationClass();
    return this.showSearchBox;
  }

  getSearchBoxAnimationClass() {
    return {
      'search-active': this.showSearchBox,
      'search-inactive': !this.showSearchBox,
    };
  }
  getSignInAnimationClass() {
    return {
      'signin-active': this.showSignIn,
      'signin-inactive': !this.showSignIn,
    };
  }
  showOrHideSignIn(clickedOutside?: string) {
    this.showSignIn = !this.showSignIn;
    if (clickedOutside && this.showSignIn) {
      this.showSignIn = false;
    }
    this.getSignInAnimationClass();
    return this.showSignIn;
  }
}
